'use client';

import * as React from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { useAdminApplePayRegistrations } from '@/lib/api/apple-pay-registrations';
import { CreateRegistrationDialog } from './components/CreateRegistrationDialog';
import { RegistrationDetailSheet } from './components/RegistrationDetailSheet';
import {
  RegistrationStatusBadge,
  ExpiryBadge,
  type ApplePayRegistration,
} from './components/utils';

/** Soonest-expiring active payment cert across a registration, for the list view. */
function soonestExpiry(registration: ApplePayRegistration): string | Date | null {
  const dates = registration.certificates
    .map((c) => c.paymentCertExpiresAt)
    .filter((d): d is NonNullable<typeof d> => !!d)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  return dates[0] ?? null;
}

export default function ApplePayByokPage() {
  const { data, isLoading, error, refetch, isRefetching } = useAdminApplePayRegistrations();
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const registrations = data ?? [];
  const selected = registrations.find((r) => r.id === selectedId) ?? null;

  const openDetail = (id: string) => {
    setSelectedId(id);
    setSheetOpen(true);
  };

  const summary = {
    total: registrations.length,
    active: registrations.filter((r) => r.status === 'active').length,
    pending: registrations.filter((r) => r.status.startsWith('pending')).length,
    attention: registrations.filter((r) => r.status === 'expired' || r.status === 'disabled').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <p className="text-muted-foreground max-w-2xl text-sm">
          Manage Apple Pay certificates. Create a <strong>platform (shared)</strong> registration — one
          certificate whose domains span many merchants, so merchants who don&apos;t supply their own are
          covered — or a merchant-owned registration. Upload certificates, then add and verify domains
          (attributing each to its merchant account). Independent of the platform-managed Apple Pay domain
          pool.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <CreateRegistrationDialog />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {!isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Registrations', value: summary.total },
            { label: 'Active', value: summary.active },
            { label: 'Pending setup', value: summary.pending },
            { label: 'Needs attention', value: summary.attention },
          ].map((c) => (
            <Card key={c.label}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{c.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{c.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : registrations.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground text-sm">
              No BYOK Apple Pay registrations yet. Create one to onboard a merchant&apos;s own certificates.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Merchant identifier</TableHead>
                  <TableHead>Display name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Certs</TableHead>
                  <TableHead>Soonest expiry</TableHead>
                  <TableHead>Domains</TableHead>
                  <TableHead>Scope</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registrations.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => openDetail(r.id)}>
                    <TableCell className="font-mono text-sm">{r.merchantIdentifier}</TableCell>
                    <TableCell>{r.displayName}</TableCell>
                    <TableCell>
                      <RegistrationStatusBadge status={r.status} />
                    </TableCell>
                    <TableCell>{r.certificates.length}</TableCell>
                    <TableCell>
                      <ExpiryBadge value={soonestExpiry(r)} />
                    </TableCell>
                    <TableCell>
                      {r.domains.filter((d) => d.active).length}/{r.domains.length}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {r.isPlatform ? 'Platform (shared)' : `Account ${r.accountId ?? ''}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <RegistrationDetailSheet registration={selected} open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
}
